<script setup lang="ts">
    import { inject, onMounted, ref, nextTick } from 'vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { HealthManagerAccess } from './HealthManager.access';
    import { TaskErrorStatus, TaskErrorComment, OrderOptions, OrderDirection, OrderOption, TaskError } from './HealthManager.types';
    import Tabs from '@shared/components/Tabs.vue';
    import tab from '@shared/components/Tab.vue';
    import TaskErrorList from './TaskErrorList.vue';
    import ChildErrorList from './ChildErrorList.vue';
    import LongRunningTaskList from './LongRunningTaskList.vue';
    import CrashedTaskList from './CrashedTaskList.vue';
    import LastModifiedList from './LastModifiedList.vue';
    import StoppedJourneyList from './StoppedJourneyList.vue';
    import Pagination from '@shared/components/Pagination/Pagination.vue';
    import Loading from '@shared/components/Loading.vue';

    const injectorAccess = inject(injectorAccessInjectionKey);

    if (!injectorAccess) {
        throw new Error('Injector access is not available');
    }

    const healthManagerAccess: HealthManagerAccess = await injectorAccess.injector.get(HealthManagerAccess);

    const orderOptions: OrderOption[] = [
        { key: OrderOptions.LastModified, display: 'Last Modified' },
        { key: OrderOptions.Created, display: 'Error Date' },
        { key: OrderOptions.Account, display: 'Account' },
    ];

    const outerTabIndex = ref<number | undefined>(undefined);

    onMounted(async () => {
        handleLoadTaskErrors(TaskErrorStatus.New);
        await healthManagerAccess.loadTaskErrorHandlers();
    });

    const handleStatusChange = async (taskError: TaskError, status: TaskErrorStatus) => {
        healthManagerAccess.state.loading = true;
        await healthManagerAccess.updateTaskErrorProperty(taskError.TaskErrorId, 'status', status.toString());
        healthManagerAccess.state.loading = false;
    };

    const handleFilterUpdate = async (filter: string) => {
        if (healthManagerAccess.state.loading) return;
        healthManagerAccess.state.loading = true;
        healthManagerAccess.state.freeTextFilter = filter;
        healthManagerAccess.state.currentTaskErrorPage = 1;
        await healthManagerAccess.updateCurrentView();
        healthManagerAccess.state.loading = false;
    };

    const handleMyTasksFilterUpdate = async (onlyMyTasks: boolean) => {
        if (healthManagerAccess.state.loading) return;
        healthManagerAccess.state.loading = true;
        healthManagerAccess.state.onlyMyTasks = onlyMyTasks;
        healthManagerAccess.state.currentTaskErrorPage = 1;
        await healthManagerAccess.updateCurrentView();
        healthManagerAccess.state.loading = false;
    };

    const handleAssignToMe = async (taskError: TaskError) => {
        healthManagerAccess.state.loading = true;
        await healthManagerAccess.assignTaskErrorToMe(taskError.TaskErrorId);
        healthManagerAccess.state.loading = false;
        handleLoadTaskErrors(healthManagerAccess.state.currentStatus);
    };

    const handleAssignToUser = async (taskError: TaskError, userId: string) => {
        healthManagerAccess.state.loading = true;
        await healthManagerAccess.assignTaskErrorToUser(taskError.TaskErrorId, userId);
        healthManagerAccess.state.loading = false;
        handleLoadTaskErrors(healthManagerAccess.state.currentStatus);
    };

    const handleTaskError = async (taskError: TaskError, handler: string) => {
        healthManagerAccess.state.loading = true;
        await healthManagerAccess.handleTaskError(handler, taskError.TaskErrorId);
        healthManagerAccess.state.loading = false;
    };

    const getTaskErrorStatuses = () => {
        return Object.entries(TaskErrorStatus)
            .filter(([key]) => isNaN(Number(key)))
            .map(([key, value]) => ({
                name: key,
                value: Number(value),
            }));
    };

    const handleLoadTaskErrors = async (status: TaskErrorStatus) => {
        healthManagerAccess.state.loading = true;
        healthManagerAccess.state.currentStatus = status;
        healthManagerAccess.state.currentTaskErrorPage = 1;

        if (status === TaskErrorStatus.Solved) {
            healthManagerAccess.state.orderByValue = OrderOptions.LastModified;
            healthManagerAccess.state.orderByDirection = OrderDirection.Desc;
        } else {
            healthManagerAccess.state.orderByValue = OrderOptions.Created;
            healthManagerAccess.state.orderByDirection = OrderDirection.Desc;
        }

        await healthManagerAccess.getTaskErrors(
            status,
            healthManagerAccess.state.currentTaskErrorPage,
            healthManagerAccess.state.taskErrorPageSize,
            healthManagerAccess.state.orderByValue,
            healthManagerAccess.state.orderByDirection,
        );

        healthManagerAccess.state.loading = false;
    };

    const saveComments = async (id: string, comments: TaskErrorComment[]) => {
        if (healthManagerAccess.state.loading) return;
        healthManagerAccess.state.loading = true;

        await healthManagerAccess.saveComment(id, comments);
        healthManagerAccess.state.loading = false;
    };

    const handlePageChange = async (newPage: number) => {
        if (healthManagerAccess.state.loading) return;
        healthManagerAccess.state.loading = true;
        healthManagerAccess.state.currentTaskErrorPage = newPage;
        await healthManagerAccess.updateCurrentView();
        healthManagerAccess.state.loading = false;
    };

    const switchAccount = async (accountId: string) => {
        if (healthManagerAccess.state.loading) return;
        healthManagerAccess.state.loading = true;

        await healthManagerAccess.switchToAccountById(accountId);
        healthManagerAccess.state.loading = false;
    };

    const handleOrderUpdate = async (orderByValue: OrderOptions, orderByDirection: OrderDirection) => {
        healthManagerAccess.state.loading = true;

        await healthManagerAccess.getTaskErrors(
            healthManagerAccess.state.currentStatus,
            healthManagerAccess.state.currentTaskErrorPage,
            healthManagerAccess.state.taskErrorPageSize,
            orderByValue,
            orderByDirection,
        );

        healthManagerAccess.state.loading = false;
    };

    const loadContextEntries = async (contextId: string) => {
        if (healthManagerAccess.state.loading) return;
        healthManagerAccess.state.loading = true;
        await healthManagerAccess.loadContextEntries(contextId);
        healthManagerAccess.state.loading = false;
    };

    const loadBackOfficeUsers = async () => {
        if (healthManagerAccess.state.backOfficeUsers) return;
        await healthManagerAccess.loadBackOfficeUsers();
    };

    const handleReturn = () => {
        healthManagerAccess.state.selectedContextId = null;
        healthManagerAccess.updateCurrentView();
    };

    const handleLastModifiedPageChange = async (page: number) => {
        if (healthManagerAccess.state.loading) return;
        await healthManagerAccess.loadLastModified(page);
    };

    const handleCreateTaskError = async (taskId: string) => {
        await healthManagerAccess.createTaskErrorForStoppedJourney(taskId);
    };

    const handleOpenTaskError = async (taskId: string, errorCode: string) => {
        const existing = healthManagerAccess.state.taskErrors.find(
            te => te.TaskId === taskId && te.ErrorCode === errorCode
        );
        if (existing) {
            await loadContextEntries(existing.TaskErrorId);
            outerTabIndex.value = undefined;
            await nextTick();
            outerTabIndex.value = 0;
        }
    };

    const handleLastModifiedFilterChange = async (filter: string) => {
        if (healthManagerAccess.state.loading) return;
        healthManagerAccess.state.lastModifiedFilter = filter;
        healthManagerAccess.state.currentLastModifiedPage = 1;
        await healthManagerAccess.loadLastModified(1);
    };

    const handleSelectErrorCode = async (errorCode: string) => {
        if (healthManagerAccess.state.loading) return;
        healthManagerAccess.state.loading = true;
        healthManagerAccess.state.selectedErrorCode = errorCode;
        healthManagerAccess.state.currentTaskErrorPage = 1;
        await healthManagerAccess.updateCurrentView();
        healthManagerAccess.state.loading = false;
    
    }
    const handleSelectErrorCodeAllContexts = async (errorCode: string) => {
        if (healthManagerAccess.state.loading) return;
        healthManagerAccess.state.loading = true;
        healthManagerAccess.state.selectedAllContextsErrorCode = errorCode;
        healthManagerAccess.state.currentAllContextsPage = 1;
        await healthManagerAccess.loadAllContexts();
        healthManagerAccess.state.loading = false;
    }


</script>

<template>
    <h2 class="m-lg-b">Application health</h2>

    <tabs :queryParam="false" navClass="navbar-tabs list-unstyled d-inline-flex" :selectedIndex="outerTabIndex">
        <tab title="Task Errors">
            <div v-show="healthManagerAccess.state.selectedContextId === null">
                <h3>Task Errors</h3>

                <tabs :queryParam="false">
                    <tab v-for="status in getTaskErrorStatuses()" :key="status.value" :title="status.name" @tabClick="handleLoadTaskErrors(status.value)">
                        <TaskErrorList
                            v-if="healthManagerAccess.state.currentStatus === status.value"
                            :taskErrors="healthManagerAccess.state.taskErrors"
                            :getTaskErrorStatuses="getTaskErrorStatuses"
                            :orderOptions="orderOptions"
                            :orderByValue="healthManagerAccess.state.orderByValue"
                            :orderByDirection="healthManagerAccess.state.orderByDirection"
                            :backOfficeUsers="healthManagerAccess.state.backOfficeUsers"
                            :taskErrorHandlers="healthManagerAccess.state.taskErrorHandlers"
                            :loading="healthManagerAccess.state.loading"
                            :onlyMyTasks="healthManagerAccess.state.onlyMyTasks"
                            :errorCodeCounts="healthManagerAccess.state.errorCodeCounts"
                            @saveComments="saveComments"
                            @switchAccount="switchAccount"
                            @update:order="handleOrderUpdate"
                            @loadContextEntries="loadContextEntries"
                            @loadBackOfficeUsers="loadBackOfficeUsers"
                            @assignToUser="handleAssignToUser"
                            @assignToMe="handleAssignToMe"
                            @statusChange="handleStatusChange"
                            @handleTaskError="handleTaskError"
                            @update:filter="handleFilterUpdate"
                            @update:myTasksFilter="handleMyTasksFilterUpdate" 
                            @update:errorCode="handleSelectErrorCode" />
                    </tab>
                </tabs>

                <Pagination
                    :totalItems="healthManagerAccess.state.totalNumberOfTaskErrors"
                    :pageSize="healthManagerAccess.state.taskErrorPageSize"
                    :currentPage="healthManagerAccess.state.currentTaskErrorPage"
                    :isLoading="healthManagerAccess.state.loading"
                    @page-changed="handlePageChange" />
            </div>

            <div v-show="healthManagerAccess.state.selectedContextId !== null">
                <h3 class="m-lg-b">
                    Error occurrences for context "{{ healthManagerAccess.state.selectedContextId }}"
                </h3>

                <button class="btn btn-default m-lg-b" @click="handleReturn">
                    <span class="glyphicon glyphicon-chevron-left"></span>
                    Return
                </button>

                <ChildErrorList
                    :entries="healthManagerAccess.state.contextEntries"
                    :loading="healthManagerAccess.state.loading" />
            </div>
        </tab>

        <tab title="Long Running Tasks" @tabClick="healthManagerAccess.loadLongRunningTasks()">
            <LongRunningTaskList :tasks="healthManagerAccess.state.longRunningTasks" />
        </tab>
        <tab title="Crashed Tasks" @tabClick="healthManagerAccess.loadCrashedTasks()">
            <CrashedTaskList :tasks="healthManagerAccess.state.crashedTasks" />
        </tab>
        <tab title="Stopped journeys" @tabClick="healthManagerAccess.loadStoppedJourneys()">
            <StoppedJourneyList
                :stoppedJourneys="healthManagerAccess.state.stoppedJourneys"
                :from="healthManagerAccess.state.stoppedJourneysFrom"
                :to="healthManagerAccess.state.stoppedJourneysTo"
                :accountFilter="healthManagerAccess.state.stoppedJourneysAccountFilter"
                :loading="healthManagerAccess.state.loading"
                @update:from="(v) => healthManagerAccess.loadStoppedJourneys(v, undefined)"
                @update:to="(v) => healthManagerAccess.loadStoppedJourneys(undefined, v)"
                @update:accountFilter="(v) => (healthManagerAccess.state.stoppedJourneysAccountFilter = v)"
                @createTaskError="handleCreateTaskError"
                @openTaskError="handleOpenTaskError" />
        </tab>
        <tab title="Last Modified" @tabClick="healthManagerAccess.loadLastModified()">
            <LastModifiedList
                :taskErrors="healthManagerAccess.state.lastModifiedTaskErrors"
                :loading="healthManagerAccess.state.loading"
                :totalItems="healthManagerAccess.state.totalLastModified"
                :pageSize="healthManagerAccess.state.lastModifiedPageSize"
                :currentPage="healthManagerAccess.state.currentLastModifiedPage"
                @pageChanged="handleLastModifiedPageChange"
                @filterChanged="handleLastModifiedFilterChange"
                @switchAccount="switchAccount" />
        </tab>
        <tab title="Total list" @tabClick="healthManagerAccess.loadAllContexts()">
            <TaskErrorList
                :taskErrors="healthManagerAccess.state.allContexts"
                :getTaskErrorStatuses="getTaskErrorStatuses"
                :orderOptions="orderOptions"
                :orderByValue="healthManagerAccess.state.orderByValue"
                :orderByDirection="healthManagerAccess.state.orderByDirection"
                :backOfficeUsers="healthManagerAccess.state.backOfficeUsers"
                :taskErrorHandlers="healthManagerAccess.state.taskErrorHandlers"
                :loading="healthManagerAccess.state.loading"
                :onlyMyTasks="healthManagerAccess.state.onlyMyTasks"
                :errorCodeCounts="healthManagerAccess.state.allContextsErrorCodeCounts"
                @saveComments="saveComments"
                @switchAccount="switchAccount"
                @update:order="handleOrderUpdate"
                @loadContextEntries="loadContextEntries"
                @loadBackOfficeUsers="loadBackOfficeUsers"
                @assignToUser="handleAssignToUser"
                @assignToMe="handleAssignToMe"
                @statusChange="handleStatusChange"
                @handleTaskError="handleTaskError"
                @update:filter="handleFilterUpdate"
                @update:myTasksFilter="handleMyTasksFilterUpdate"
                @update:errorCode="handleSelectErrorCodeAllContexts" />
            <Pagination
                :totalItems="healthManagerAccess.state.totalAllContexts"
                :pageSize="healthManagerAccess.state.allContextsPageSize"
                :currentPage="healthManagerAccess.state.currentAllContextsPage"
                :isLoading="healthManagerAccess.state.loading"
                @page-changed="(page) => healthManagerAccess.loadAllContexts(page)" />
        </tab>
    </tabs>

    <Loading :show="healthManagerAccess.state.loading" :loadingStyle="'progress-bar'" :fixed="true" />
</template>
